import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import CaptiveDialog from 'features/rdc/components/CaptiveDialog/CaptiveDialog';
import useRDCNavigator from 'features/rdc/hooks/useRDCNavigator';
import useRDCOptions from 'features/rdc/hooks/useRDCOptions';
import {
  CheckImageType,
  RDCContext,
} from 'features/rdc/providers/RDCContextProvider/RDCContextProvider';
import { RDCSearchParams } from 'features/rdc/RDCSearchParams';
import rdcStrings from 'features/rdc/rdcStrings.en';
import { useFetchAccountDetails } from 'features/rdc/screens/Amount/hooks/useFetchAccountDetails';
import DepositCheckSuccessView from 'features/rdc/screens/ReviewDeposit/DepositCheckSuccessView';
import useSubmitCheckDeposit, {
  SubmitDepositCheckError,
} from 'features/rdc/screens/ReviewDeposit/hooks/useSubmitCheckDeposit';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { isWeb } from 'shared/constants/platform';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import theme from 'shared/styles/theme';
import { CoreError } from 'shared/utils/errors/CoreError';
import { formatSizeUnits } from 'shared/utils/formatSizeUnits/formatSizeUnits';
import { CombinedError } from 'urql';

import ErrorOrOutcomeView from './ErrorOrOutcomeView';
import formatScreenContent from './formatScreenContent';

export default function ReviewDeposit() {
  const router = useRouter();
  const { session, account, amount, depositFlowId } =
    useLocalSearchParams<RDCSearchParams>();
  const { getImage } = useContext(RDCContext);
  const { restart, finish } = useRDCNavigator();
  const { rdcAvailable } = useRDCOptions();
  const {
    isFetching,
    submitDepositCheckResult,
    submitDepositCheckError,
    submitCheckDeposit,
    sendCheckAsIs,
  } = useSubmitCheckDeposit(depositFlowId);
  const { response } = useFetchAccountDetails();
  const { reviewDeposit } = rdcStrings;
  const { t: translatedText } = useTranslation('rdc');
  const [modalVisible, setModalVisible] = useState(false);
  const [errorModel, setErrorModel] = useState<
    SubmitDepositCheckError | CombinedError | CoreError | null
  >(null);
  const defaultError = useDefaultError();
  const [imageSizes, setImageSizes] = useState<{
    front_img_size: string;
    back_img_size: string;
  } | null>(null);

  const { bottom } = useSafeAreaInsets();

  useEffect(() => {
    if (submitDepositCheckError) {
      setErrorModel(submitDepositCheckError);
    }
  }, [setErrorModel, submitDepositCheckError]);

  const logRdcExitedOnDepositConfirmEvent = usePressableAnalytics({
    analyticsId: 'rdcCaptureExited',
    customAttributes: { source_name: 'check_deposit_confirm' },
  });

  const logRdcExitedOnDepositFailureEvent = usePressableAnalytics({
    analyticsId: 'rdcCaptureExited',
    customAttributes: { source_name: 'check_deposit_flow_failed' },
  });

  const handleBackButtonAction = () => {
    setModalVisible(true);
  };

  useBackHandler(() => {
    handleBackButtonAction();
  });

  const sectionsData = useMemo(() => {
    const handleChangeAmountAction = () => {
      router.dismissTo({
        pathname: `./amount`,
        params: {
          session,
          account,
          amount,
          mode: 'edit-amount',
          depositFlowId,
        },
      });
    };

    const editImageFront = () => {
      // always push new screen
      router.dismissTo({
        pathname: './capture',
        params: {
          session,
          account,
          amount,
          mode: 'edit-front',
          depositFlowId,
        },
      });
    };

    const editImageBack = () => {
      // always push new screen
      router.dismissTo({
        pathname: './capture',
        params: {
          session,
          account,
          amount,
          mode: 'edit-back',
          depositFlowId,
        },
      });
    };

    const selectedAccountTitle = (accountID?: string) => {
      if (accountID) {
        const selectedAccount = response?.find(
          (account) => account.id === accountID,
        );
        if (selectedAccount) {
          return `${selectedAccount.nickname} • ${selectedAccount.accountNumberLast4}`;
        }
      }
      return '';
    };

    return formatScreenContent({
      accountTitle: selectedAccountTitle(account),
      amount,
      session,
      handleChangeAmountAction,
      editImageFront,
      editImageBack,
      getImage,
      translatedText,
      depositFlowId,
    });
  }, [
    account,
    amount,
    session,
    getImage,
    translatedText,
    response,
    depositFlowId,
    router,
  ]);

  if (!rdcAvailable) {
    return <Redirect href="./" />;
  }

  const handleDepositCheckAction = async () => {
    const frontCheckImageUri = getCheckImageUri('front');
    const backCheckImageUri = getCheckImageUri('back');
    if (
      session &&
      account &&
      amount &&
      frontCheckImageUri &&
      backCheckImageUri
    ) {
      await fetchImageSizes(frontCheckImageUri, backCheckImageUri);
      await submitCheckDeposit({
        session,
        accountId: account,
        amount,
        frontImageURI: frontCheckImageUri,
        backImageURI: backCheckImageUri,
      });
    } else {
      setErrorModel(defaultError);
    }
  };

  const getCheckImageUri = (imageType: CheckImageType): string | null => {
    const imageSource = getImage(depositFlowId, imageType)?.source;

    if (imageSource) {
      return Image.resolveAssetSource(imageSource).uri;
    }

    return null;
  };

  const fetchImageSizes = async (
    frontCheckImageUri: string,
    backCheckImageUri: string,
  ) => {
    try {
      // Fetch sizes concurrently
      const [frontImageSize, backImageSize] = await Promise.all([
        getImageSize(frontCheckImageUri),
        getImageSize(backCheckImageUri),
      ]);

      setImageSizes({
        front_img_size: frontImageSize ?? '',
        back_img_size: backImageSize ?? '',
      });
    } catch (error) {
      // Handle error silently
      console.error('Error fetching image sizes:', error);
    }
  };

  const getImageSize = async (uri: string) => {
    try {
      // Check if it's a data URI
      if (uri.startsWith('data:')) {
        // Extract base64 data
        const base64Data = uri.split(',')[1];
        if (base64Data) {
          // Calculate size: base64 encoding increases size by ~33%
          // Remove padding characters to get accurate count
          const base64Length = base64Data.replace(/=/g, '').length;
          // Approximate binary size: (base64Length * 3) / 4
          const sizeInBytes = Math.floor((base64Length * 3) / 4);
          return formatSizeUnits(sizeInBytes);
        }
      } else {
        // Fetch the image as a blob for regular URIs
        const response = await fetch(uri);
        const blob = await response.blob();
        const sizeInBytes = blob.size;
        return formatSizeUnits(sizeInBytes);
      }
    } catch (error) {
      // Handle error silently
      console.error('Error fetching image size:', error);
    }
  };

  const handleFundsAvailabilityAndTermsAction = () => {
    router.push({
      pathname: '/move-money/check-deposit/agreements',
    });
  };

  const handleReviewOrUpdateCheckAction = () => {
    setErrorModel(null);
  };

  const handleSendCheckAsIsAction = async (rdcUploadId?: string) => {
    if (rdcUploadId) {
      await sendCheckAsIs(rdcUploadId);
    }
  };

  const handleDoneAction = () => {
    finish();
  };

  const handleCancelAction = () => {
    setModalVisible(true);
  };

  const handleRetryAction = async () => {
    setErrorModel(null);
    await handleDepositCheckAction();
  };

  const handleDepositAnotherAction = () => {
    restart();
  };

  if (isWeb) {
    return <Redirect href="/move-money/check-deposit" />;
  }

  if (submitDepositCheckResult) {
    return (
      <DepositCheckSuccessView
        amount={amount ? amount : ''}
        confirmationNumber={submitDepositCheckResult.confirmationNumber}
        translatedText={translatedText}
        handleFundsAvailabilityAction={handleFundsAvailabilityAndTermsAction}
        handleMobileCheckDepositTermsAction={
          handleFundsAvailabilityAndTermsAction
        }
        handleDoneAction={handleDoneAction}
        handleDepositAnotherCheckAction={{
          onPress: handleDepositAnotherAction,
          analytics: {
            analyticsId: 'mobileCheckDepositClicked',
            customAttributes: {
              source_name: 'check_deposit_in_review',
              front_img_size: imageSizes?.front_img_size ?? '',
              back_img_size: imageSizes?.back_img_size ?? '',
            },
          },
        }}
      />
    );
  } else if (errorModel) {
    return (
      <View style={baseStyles.errorcontainer}>
        <ErrorOrOutcomeView
          errorModel={errorModel}
          translatedText={translatedText}
          defaultError={defaultError}
          isLoading={isFetching}
          handleReviewOrUpdateCheckAction={handleReviewOrUpdateCheckAction}
          handleSendCheckAsIsAction={(rdcUploadId) =>
            handleSendCheckAsIsAction(rdcUploadId)
          }
          handleDoneAction={handleDoneAction}
          handleCancelAction={handleCancelAction}
          handleRetryAction={handleRetryAction}
        />
        <CaptiveDialog
          isDialogVisible={modalVisible}
          setModalVisible={setModalVisible}
          handlePrimaryAction={() => {
            logRdcExitedOnDepositFailureEvent();
            finish();
          }}
          handleSecondaryAction={(value) => setModalVisible(value)}
        />
      </View>
    );
  }

  return (
    <View style={baseStyles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: false,
          title: translatedText('reviewDeposit.title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackButtonAction}
            />
          ),
        }}
      />
      <SectionedContentScreen model={sectionsData} initialNumToRender={25} />
      <View>
        <HairLineDivider />
        <View
          style={[
            baseStyles.buttonContainer,
            { paddingBottom: bottom + theme.spacing.m },
          ]}
        >
          <Button
            label={translatedText(
              'reviewDeposit.footerSection.buttons.depositCheck',
              reviewDeposit.footerSection.buttons.depositCheck,
            )}
            type="PRIMARY"
            size="LARGE"
            isLoading={isFetching}
            analyticsId="rdcCheckSubmitted"
            customAttributes={{ source_name: 'check_deposit_confirm' }}
            onPress={handleDepositCheckAction}
          />
          <Button
            label={translatedText(
              'reviewDeposit.footerSection.buttons.cancel',
              reviewDeposit.footerSection.buttons.cancel,
            )}
            type="SECONDARY"
            size="LARGE"
            onPress={() => setModalVisible(true)}
          />
          <CaptiveDialog
            isDialogVisible={modalVisible}
            setModalVisible={setModalVisible}
            handlePrimaryAction={() => {
              logRdcExitedOnDepositConfirmEvent();
              finish();
            }}
            handleSecondaryAction={(value) => setModalVisible(value)}
          />
        </View>
      </View>
    </View>
  );
}

const baseStyles = StyleSheet.create({
  buttonContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  errorcontainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    padding: theme.spacing.m,
  },
});
